import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { LocalService } from '../../../services/local.service';

type ParticipantSortMode = 'name' | 'standing';

@Component({
  selector: 'app-participants-list',
  templateUrl: './participants-list.component.html',
  styleUrls: ['./participants-list.component.scss'],
  standalone: false,
})
export class ParticipantsListComponent implements OnInit {
  @Input() users: any[] = [];
  @Input() positionByUserId: Record<string, number> = {};
  @Output() userSelected = new EventEmitter<any>();

  searchTerm = '';
  sortMode: ParticipantSortMode = 'name';
  private readonly sortModeStorageKey = 'participantsSortMode';
  private readonly plainSortModeStorageKey = 'wc26:participantsSortMode';

  constructor(
    private localService: LocalService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    const savedSortMode = this.getPlainSavedSortMode();
    if (savedSortMode) {
      this.sortMode = savedSortMode;
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.getPlainSavedSortMode()) {
      return;
    }

    const savedSortMode =
      await this.localService.getDataV2<ParticipantSortMode>(
        this.sortModeStorageKey,
      );

    if (savedSortMode === 'name' || savedSortMode === 'standing') {
      this.sortMode = savedSortMode;
      this.savePlainSortMode(savedSortMode);
      this.changeDetectorRef.markForCheck();
    }
  }

  get filteredUsers(): any[] {
    const normalizedSearch = this.normalize(this.searchTerm);

    return [...(this.users ?? [])]
      .filter((user) =>
        this.normalize(user?.name).includes(normalizedSearch),
      )
      .sort((left, right) => this.compareUsers(left, right));
  }

  async setSortMode(sortMode: ParticipantSortMode): Promise<void> {
    this.sortMode = sortMode;
    this.savePlainSortMode(sortMode);
    await this.localService
      .saveDataV2(this.sortModeStorageKey, sortMode)
      .catch(() => undefined);
  }

  getPosition(user: any): number | null {
    return this.positionByUserId?.[user?._id] ?? null;
  }

  selectUser(user: any): void {
    this.userSelected.emit(user);
  }

  private compareUsers(left: any, right: any): number {
    if (this.sortMode === 'standing') {
      const leftPosition = this.getPosition(left) ?? Number.MAX_SAFE_INTEGER;
      const rightPosition = this.getPosition(right) ?? Number.MAX_SAFE_INTEGER;

      if (leftPosition !== rightPosition) {
        return leftPosition - rightPosition;
      }
    }

    return String(left?.name ?? '').localeCompare(String(right?.name ?? ''), 'es');
  }

  private normalize(value: any): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private getPlainSavedSortMode(): ParticipantSortMode | null {
    const value = localStorage.getItem(this.plainSortModeStorageKey);
    return value === 'name' || value === 'standing' ? value : null;
  }

  private savePlainSortMode(sortMode: ParticipantSortMode): void {
    localStorage.setItem(this.plainSortModeStorageKey, sortMode);
  }
}
